<template>
  <div class="min-h-screen hero-gradient text-slate-600 docs-page">
    <!-- Fixed Header - No transition -->
    <LandingHeader :brand-name="globalConfig.BRAND_NAME || 'HiCHAT'" brand-url="/" primary-cta-url="/app/auth/signup" :nav-links="navLinks" />

    <!-- Main Content - Only this part transitions -->
    <div class="docs-layout page-content">
      <!-- Desktop/large screens: static sidebar -->
      <aside class="docs-sidebar hidden lg:block">
        <div class="docs-sidebar-inner">
          <h3 class="docs-sidebar-title">
            {{ currentPageType === 'docs' ? 'Hướng dẫn' : 'Tính năng' }}
          </h3>
          <nav class="docs-nav">
            <DocsTreeNode v-for="page in pagesTree" :key="page.id" :page="page" :current-slug="currentPage?.full_path || currentPage?.slug || ''" :page-type="currentPageType" :depth="0" />
          </nav>
        </div>
      </aside>

      <main class="docs-main">
        <div class="docs-content">
          <!-- Mobile/Tablet: TOC toggle button -->
          <div class="mb-4 lg:hidden">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
              @click="showToc = true"
            >
              <span>Danh mục nội dung</span>
            </button>
          </div>

          <!-- Mobile/Tablet: slide-over sidebar -->
          <transition name="fade">
            <div v-if="showToc" class="fixed inset-0 z-50 lg:hidden">
              <!-- Backdrop -->
              <div class="absolute inset-0 bg-black/30" @click="showToc = false"></div>
              <!-- Panel -->
              <div class="absolute inset-y-0 left-0 w-[85vw] max-w-[20rem] bg-white dark:bg-slate-800 shadow-xl border-r border-slate-200 dark:border-slate-700 flex flex-col">
                <div class="px-4 py-3 flex items-center justify-between">
                  <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {{ currentPageType === 'docs' ? 'Hướng dẫn' : 'Tính năng' }}
                  </h3>
                  <button class="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white" aria-label="Đóng" @click="showToc = false">✕</button>
                </div>
                <div class="flex-1 overflow-y-auto p-2">
                  <nav class="docs-nav">
                    <DocsTreeNode
                      v-for="page in pagesTree"
                      :key="page.id"
                      :page="page"
                      :current-slug="currentPage?.full_path || currentPage?.slug || ''"
                      :page-type="currentPageType"
                      :depth="0"
                      dense
                    />
                  </nav>
                </div>
              </div>
            </div>
          </transition>

          <DocsBreadcrumb v-if="currentPage?.breadcrumb && currentPage.breadcrumb.length > 0" :breadcrumb="currentPage.breadcrumb" :page-type="currentPageType" />

          <article class="docs-article transition-all duration-300 ease-in-out" :class="{ loading: loading }">
            <div v-if="loading" class="docs-loading">
              <div class="docs-loading-spinner"></div>
              <p>Đang tải...</p>
            </div>
            <div v-else-if="error" class="docs-error">
              <h3>Lỗi tải trang</h3>
              <p>{{ error }}</p>
              <button class="docs-retry-btn" @click="retryLoad">Thử lại</button>
            </div>
            <div v-else-if="pageContent" class="prose" v-html="pageContent"></div>
            <div v-else-if="!currentPage && pagesTree.length > 0" class="docs-empty">
              <p>Chọn một trang từ menu bên trái để xem nội dung.</p>
            </div>
            <div v-else class="docs-empty">
              <p>Chưa có nội dung.</p>
            </div>
          </article>
        </div>
      </main>
    </div>

    <!-- Fixed Footer - No transition -->
    <LandingFooter :brand-name="globalConfig.BRAND_NAME || 'HiCHAT'" brand-url="/" :logo-url="globalConfig.LOGO_THUMBNAIL || ''" :support-email="supportEmail" :content="footerData" />
  </div>
</template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import LandingHeader from '../components/LandingHeader.vue';
import LandingFooter from '../components/LandingFooter.vue';
import DocsTreeNode from '../components/DocsTreeNode.vue';
import DocsBreadcrumb from '../components/DocsBreadcrumb.vue';

const props = defineProps({
  slug: { type: String, default: '' },
  pageType: { type: String, default: 'docs' },
});

const store = useStore();
const route = useRoute();
const showToc = ref(false);

const globalConfig = computed(() => store.getters['home/globalConfig'] || {});
const footerData = computed(() => store.getters['home/footerData']);
const pagesTree = computed(() => store.getters['docs/pagesTree']);
const currentPage = computed(() => store.getters['docs/currentPage']);
const loading = computed(() => store.getters['docs/loading']);
const error = computed(() => store.getters['docs/error']);
const pageContent = computed(() => currentPage.value?.rich_text_content?.body || '');

const currentPageType = computed(() => {
  return route.path.startsWith('/features') ? 'features' : 'docs';
});

const currentSlug = computed(() => {
  if (Array.isArray(props.slug)) {
    return props.slug.join('/') || '';
  }
  return props.slug || '';
});

const navLinks = computed(() => [
  { to: '/#challenge', label: 'Vấn đề', hash: true },
  { to: '/#solution', label: 'Giải pháp', hash: true },
  { to: '/#faq', label: 'Hỏi đáp', hash: true },
  { to: '/#pricing', label: 'Báo giá', hash: true },
  { to: '/docs', label: 'Hướng dẫn' },
]);

onMounted(async () => {
  await store.dispatch('locale/initLocale');
  await store.dispatch('home/fetchContentWithCache');

  const existingTree = store.getters['docs/pagesTree'];
  if (!existingTree || existingTree.length === 0) {
    await store.dispatch('docs/fetchTree', currentPageType.value);
  }

  if (currentSlug.value) {
    await store.dispatch('docs/fetchPage', {
      slug: currentSlug.value,
      pageType: currentPageType.value,
    });
  } else {
    // Show tree only, no specific page
    // Optionally redirect to first page
  }
});

// Watch for page type changes (docs <-> features)
watch(
  () => route.path,
  async (newPath, oldPath) => {
    const newPageType = newPath.startsWith('/features') ? 'features' : 'docs';
    let oldPageType = null;
    if (oldPath) {
      oldPageType = oldPath.startsWith('/features') ? 'features' : 'docs';
    }

    // Only fetch tree if switching between docs/features
    if (oldPageType && oldPageType !== newPageType) {
      store.commit('docs/SET_LOADING', true);
      store.commit('docs/CLEAR_CURRENT_PAGE');
      await store.dispatch('docs/fetchTree', newPageType);
    }
  },
  { immediate: false }
);

// Watch for slug changes (sidebar navigation)
watch(
  () => route.params.slug,
  async newSlug => {
    if (newSlug) {
      const slugStr = Array.isArray(newSlug) ? newSlug.join('/') : newSlug;
      const currentPageSlug = currentPage.value?.slug;

      // Only fetch if slug is different
      if (slugStr !== currentPageSlug) {
        store.commit('docs/SET_LOADING', true);
        store.commit('docs/CLEAR_CURRENT_PAGE');
        const pageType = route.path.startsWith('/features') ? 'features' : 'docs';
        await store.dispatch('docs/fetchPage', { slug: slugStr, pageType });
      }
    } else {
      // No slug, clear current page
      store.commit('docs/CLEAR_CURRENT_PAGE');
    }
    // Close TOC drawer on navigation in mobile
    showToc.value = false;
  },
  { immediate: false }
);

// Retry function
const retryLoad = async () => {
  store.commit('docs/SET_ERROR', null);
  if (currentSlug.value) {
    await store.dispatch('docs/fetchPage', {
      slug: currentSlug.value,
      pageType: currentPageType.value,
    });
  } else {
    await store.dispatch('docs/fetchTree', currentPageType.value);
  }
};
</script>

<style scoped>
.docs-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.docs-loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.docs-error {
  text-align: center;
  padding: 3rem;
  color: #dc2626;
}

.docs-error h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.docs-retry-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  margin-top: 1rem;
}

.docs-retry-btn:hover {
  background: #2563eb;
}

.docs-empty {
  text-align: center;
  padding: 3rem;
  color: #64748b;
}

:deep(.docs-content .prose iframe),
:deep(.docs-content .prose embed),
:deep(.docs-content .prose video) {
  display: block;
  max-width: 800px;
  width: 100%;
  margin: 2rem auto;
  border-radius: 0.75rem;
  border: none;
  box-shadow: 0 15px 40px rgba(15, 23, 42, 0.12);
}

:deep(.docs-content .prose .ql-video) {
  height: 450px;
}
</style>
